import React from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { useI18n } from "../../lib/i18n";
import { resolveImage } from "../admin/ImageUpload";
import { Reveal, SectionEyebrow, ArrowIcon } from "../common/Section";

/** Render a single block by type. Blocks are stored in Page.blocks[]. */
const Block = ({ block }) => {
  const { pick } = useI18n();
  const p = block.props || {};

  switch (block.type) {
    case "hero":
      return (
        <section className="relative h-[70vh] min-h-[440px] hero-backdrop grain overflow-hidden flex items-end" data-block="hero">
          <img src={resolveImage(p.image) || "https://images.unsplash.com/photo-1481026469463-66327c86e544"} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="relative z-10 container-x pb-16 md:pb-20 w-full text-white">
            {pick(p.eyebrow) ? <SectionEyebrow className="text-white/70">{pick(p.eyebrow)}</SectionEyebrow> : null}
            <h1 className="display display-lg mt-6 text-white max-w-4xl">{pick(p.title)}</h1>
            {pick(p.subtitle) ? <p className="mt-6 text-white/75 max-w-2xl leading-relaxed">{pick(p.subtitle)}</p> : null}
            {pick(p.cta_label) ? (
              <Link to={p.cta_url || "/"} className="btn-ghost mt-8">
                <span>{pick(p.cta_label)}</span><ArrowIcon />
              </Link>
            ) : null}
          </div>
        </section>
      );
    case "text":
      return (
        <section className="bg-white" data-block="text">
          <div className="container-x py-20 md:py-28 max-w-3xl">
            {pick(p.heading) ? <h2 className="display display-md">{pick(p.heading)}</h2> : null}
            {pick(p.body) ? <div className="mt-6 text-brand-dark leading-relaxed whitespace-pre-line">{pick(p.body)}</div> : null}
          </div>
        </section>
      );
    case "two-column":
      return (
        <section className="bg-white" data-block="two-column">
          <div className="container-x py-20 md:py-28 grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="text-brand-dark leading-relaxed whitespace-pre-line">{pick(p.left)}</div>
            <div className="text-brand-dark leading-relaxed whitespace-pre-line">{pick(p.right)}</div>
          </div>
        </section>
      );
    case "image":
      return (
        <section className="bg-white" data-block="image">
          <div className="container-x py-16">
            <img src={resolveImage(p.image)} alt={pick(p.caption)} className="w-full" />
            {pick(p.caption) ? <div className="mt-3 text-xs tracking-[0.22em] uppercase text-brand-medium">{pick(p.caption)}</div> : null}
          </div>
        </section>
      );
    case "gallery":
      return (
        <section className="bg-brand-light" data-block="gallery">
          <div className="container-x py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-6">
            {(p.images || []).map((img, i) => (
              <div key={i} className="aspect-[4/3] overflow-hidden">
                <img src={resolveImage(img)} alt="" className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        </section>
      );
    case "stats":
      return (
        <section className="bg-white" data-block="stats">
          <div className="container-x py-20 md:py-28 grid grid-cols-2 md:grid-cols-4 gap-8">
            {(p.items || []).map((s, i) => (
              <div key={i} className="border-t border-black/10 pt-6">
                <div className="font-serif text-brand-navy" style={{ fontSize: "clamp(2.4rem, 5vw, 4rem)", lineHeight: 1 }}>{s.value}</div>
                <div className="mt-3 h-px w-8 bg-brand-gold" />
                <div className="mt-3 text-[0.66rem] tracking-[0.24em] uppercase text-brand-medium">{pick(s.label)}</div>
              </div>
            ))}
          </div>
        </section>
      );
    case "cta":
      return (
        <section className={`${p.dark ? "bg-brand-navy text-white" : "bg-brand-light"} relative overflow-hidden`} data-block="cta">
          {p.dark ? <div className="grain absolute inset-0 pointer-events-none" /> : null}
          <div className="container-x py-20 md:py-28 relative grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className={`display display-lg ${p.dark ? "text-white" : "text-brand-navy"}`}>{pick(p.title)}</h2>
              {pick(p.body) ? <p className={`mt-6 ${p.dark ? "text-white/75" : "text-brand-medium"}`}>{pick(p.body)}</p> : null}
            </div>
            {pick(p.button_label) ? (
              <div>
                <Link to={p.button_url || "/contact"} className={p.dark ? "btn-ghost" : "btn-outline-dark"}>
                  <span>{pick(p.button_label)}</span><ArrowIcon />
                </Link>
              </div>
            ) : null}
          </div>
        </section>
      );
    case "cards":
      return (
        <section className="bg-white" data-block="cards">
          <div className="container-x py-20 md:py-28 grid grid-cols-1 md:grid-cols-3 gap-8">
            {(p.items || []).map((c, i) => (
              <div key={i} className="bg-white border border-black/5 p-6">
                {c.image ? <img src={resolveImage(c.image)} alt="" className="aspect-[4/3] object-cover w-full" /> : null}
                <div className="mt-4 font-serif text-2xl text-brand-navy">{pick(c.title)}</div>
                <div className="mt-3 text-brand-medium leading-relaxed">{pick(c.body)}</div>
              </div>
            ))}
          </div>
        </section>
      );
    case "faq":
      return (
        <section className="bg-white" data-block="faq">
          <div className="container-x py-20 md:py-28 max-w-3xl divide-y divide-black/10">
            {(p.items || []).map((f, i) => (
              <details key={i} className="py-6 group">
                <summary className="cursor-pointer font-serif text-xl text-brand-navy list-none flex items-center justify-between">
                  {pick(f.q)}<span className="text-brand-gold group-open:rotate-45 transition-transform">+</span>
                </summary>
                <div className="mt-4 text-brand-dark leading-relaxed">{pick(f.a)}</div>
              </details>
            ))}
          </div>
        </section>
      );
    case "html":
      return (
        <section className="bg-white" data-block="html">
          <div
            className="container-x py-16"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(p.html || "", {
                // Allow standard rich-text tags but strip <script>, event handlers,
                // and any URI schemes other than http/https/mailto/tel/data:image.
                USE_PROFILES: { html: true },
                ADD_ATTR: ["target", "rel"],
              }),
            }}
          />
        </section>
      );
    default:
      return null;
  }
};

const PageRenderer = ({ blocks }) => {
  if (!blocks?.length) return null;
  const sorted = [...blocks].sort((a, b) => (a.order || 0) - (b.order || 0));
  return (
    <>
      {sorted.map((b, i) => <Reveal key={i}><Block block={b} /></Reveal>)}
    </>
  );
};

export default PageRenderer;
